/*
 * Platform → series mapping.
 *
 * Each platform owns a fixed categorical slot, on every chart on every page.
 * Narrowing the header filter hides series; it never repaints the survivors —
 * color follows the entity, not its rank in the current view.
 *
 * The five-slot order was validated against this app's own surfaces (light
 * #ffffff, dark #151220): all checks pass in both modes, with a sub-3:1
 * contrast warning on three light-mode slots that the mandatory table view in
 * ChartFrame relieves.
 */

import type { ByPlatform, ClientKind, Platform, PlatformFilter } from '../data/types';
import { PLATFORMS, PLATFORM_CLIENT, PLATFORM_LABELS, platformsFor } from '../data/types';
import type { SeriesDef } from '../components/charts/parts';

export const PLATFORM_COLORS: ByPlatform<string> = {
  windows: 'var(--series-1)',
  macos: 'var(--series-2)',
  linux: 'var(--series-3)',
  android: 'var(--series-4)',
  ios: 'var(--series-5)',
};

const SERIES: Record<Platform, SeriesDef> = Object.fromEntries(
  PLATFORMS.map((p) => [p, { key: p, label: PLATFORM_LABELS[p], color: PLATFORM_COLORS[p] }]),
) as Record<Platform, SeriesDef>;

/** Series for the platforms the filter covers, always in canonical slot order. */
export function platformSeries(filter: PlatformFilter): SeriesDef[] {
  return platformsFor(filter).map((p) => SERIES[p]);
}

export function seriesFor(platform: Platform): SeriesDef {
  return SERIES[platform];
}

/**
 * Scales a fleet-wide metric down to the selected platforms.
 *
 * The summary metrics arrive pre-aggregated across the whole fleet, so when
 * the user narrows the filter we re-weight by those platforms' share of DAU
 * rather than pretending the fleet number applies. Ratio metrics (crash-free
 * rate, stickiness) must not be scaled — pass `ratio` for those.
 */
export function scaleToFilter(
  value: number,
  filter: PlatformFilter,
  latest: ByPlatform,
  ratio = false,
): number {
  if (filter === 'all' || ratio) return value;
  const total = PLATFORMS.reduce((sum, p) => sum + latest[p], 0);
  if (total === 0) return value;
  const covered = platformsFor(filter).reduce((sum, p) => sum + latest[p], 0);
  return value * (covered / total);
}

/** Drops rows that are entirely zero under the current filter. */
export function visibleSegments<T extends Partial<ByPlatform>>(rows: T[], filter: PlatformFilter): T[] {
  const active = platformsFor(filter);
  return rows.filter((row) => active.some((p) => (row[p] ?? 0) > 0));
}

export const FILTER_LABELS: Record<string, string> = {
  all: 'All platforms',
  desktop: 'All desktop',
  mobile: 'All mobile',
  ...PLATFORM_LABELS,
};

export function filterLabel(filter: PlatformFilter): string {
  return FILTER_LABELS[filter] ?? filter;
}

/**
 * The newest shipped build of each client, read from the data.
 *
 * The sidebar used to carry these as two hard-coded strings, which is a footgun
 * with a long fuse: `set_version` updates the apps, the apps report the new
 * build, every chart moves — and the one line claiming to say what is current
 * keeps naming last month's release. Nothing here is authored, so there is
 * nothing to forget to update.
 *
 * `isLatest` is per platform, so five rows can carry it at once; the highest
 * version among a client's platforms wins. Absent data returns null rather than
 * a guess, and the caller renders nothing.
 */
export function latestVersions(rows: { version: string; platform: Platform; isLatest: boolean }[]): Record<
  ClientKind,
  string | null
> {
  const best: Record<ClientKind, string | null> = { desktop: null, mobile: null };
  for (const row of rows) {
    if (!row.isLatest) continue;
    const client = PLATFORM_CLIENT[row.platform];
    if (!best[client] || compareVersions(row.version, best[client]!) > 0) best[client] = row.version;
  }
  return best;
}

/**
 * Semver-ish ordering, numeric segment by numeric segment.
 *
 * String comparison gets this wrong exactly when it matters most — "10.0.0" <
 * "9.9.0" lexically, so the first two-digit major release would have pinned the
 * sidebar to the previous one. Non-numeric suffixes (a `-beta`) sort below the
 * same version without one.
 */
export function compareVersions(a: string, b: string): number {
  const parse = (v: string) => v.split(/[.\-+]/).map((part) => (/^\d+$/.test(part) ? Number(part) : -1));
  const left = parse(a);
  const right = parse(b);
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    const diff = (left[i] ?? 0) - (right[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

export { PLATFORMS, PLATFORM_CLIENT, PLATFORM_LABELS, platformsFor };
export type { ClientKind, Platform, PlatformFilter };
